import { createTheme } from '@mui/material/styles';

// Custom theme inspired by the original Magellan app
const theme = createTheme({
  palette: {
    primary: {
      main: '#0A3161', // navy-dark
      light: '#15518a', // navy-medium
      dark: '#072347',
      contrastText: '#fff',
    },
    secondary: {
      main: '#D4AF37', // gold-medium
      light: '#F5E7A5', // gold-light
      dark: '#97712F', // gold-dark
      contrastText: '#0A3161',
    },
    background: {
      default: '#F4F1E6', // warm cream — deepened so white cards separate clearly
      paper: '#fff',
    },
    // Solid warm-gray divider. The old default (12% black) was nearly
    // invisible on cream/white, which is why card borders were hard to see.
    divider: '#DAD5C4',
    text: {
      primary: '#1A2639', // text-dark
      secondary: '#3A7CA5', // blue-water
    },
    error: {
      main: '#A52A2A', // red-accent
    },
    info: {
      main: '#81A4CD', // blue-light
    },
    success: {
      main: '#2E7D32',
    },
    warning: {
      main: '#e67700',
    },
  },
  typography: {
    // Body / UI: clean, warm sans. Headings: an engraved display serif that
    // gives the app an "atlas / old-world" voice instead of admin-panel default.
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    // A single reusable stack for display headings.
    h1: { fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif", fontWeight: 600, letterSpacing: '-0.01em' },
    h2: { fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif", fontWeight: 600, letterSpacing: '-0.01em' },
    h3: { fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif", fontWeight: 600 },
    h4: { fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif", fontWeight: 600 },
    h5: { fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif", fontWeight: 600 },
    h6: { fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif", fontWeight: 600 },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
  },
  shape: {
    // One radius scale used everywhere (cards step up to 14 via MuiCard below).
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          padding: '8px 16px',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 6px 10px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: '0 10px 30px rgba(10, 49, 97, 0.08)',
          overflow: 'hidden',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        // Default header is now light and calm (was a forced dark navy on EVERY
        // card, which read as heavy). Opt into the dark treatment per-instance
        // with sx, or the reusable className "card-header--dark" below.
        root: {
          padding: '16px 20px',
          borderBottom: '1px solid #DAD5C4',
          '&.card-header--dark': {
            backgroundColor: '#072347',
            color: '#fff',
            borderBottom: 'none',
          },
        },
        title: {
          fontFamily: "'Fraunces', Georgia, serif",
          fontWeight: 600,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D4AF37',
              borderWidth: 2,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#15518a',
            },
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid #ccc',
          color: '#1A2639',
          backgroundColor: '#fff',
          '&.Mui-selected': {
            backgroundColor: '#F5E7A5', // gold-medium
            color: '#0A3161', // navy-dark text for contrast
            '&:hover': {
              backgroundColor: '#D4AF37', // gold-dark on hover
            },
          },
          '&:hover': {
            backgroundColor: '#f0f0f0',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#0A3161',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: '#15518a' ,
          '&.Mui-selected': {
            color: '#D4AF37',
          },
        },
      },
    },
  },
});

export default theme;